import { blink } from '../blink';

// The former admin-db-proxy edge function was retired by the platform. Public
// DB access is enabled for this project, so use the SDK directly from the
// browser instead of calling a deployment that can no longer exist.

// ─── Safe result type ─────────────────────────────────────────────────────────

export interface SafeResult<T> {
  ok: true;
  data: T;
}
export interface SafeError {
  ok: false;
  error: string;
  data: never[];
}
export type DbResult<T> = SafeResult<T> | SafeError;
export type DbAction = 'list' | 'listIds' | 'count' | 'get' | 'upsert' | 'delete' | 'deleteAll';

function toSnakeCase(value: string): string {
  return value.includes('_') ? value : value.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

function getDbTable(table: string): any {
  return blink.db.table<any>(toSnakeCase(table));
}

// ─── SDK caller ───────────────────────────────────────────────────────────────

export async function proxyCall<T = any>(
  action: DbAction,
  table: string,
  payload?: any,
  logErrors = true,
): Promise<DbResult<T>> {
  try {
    const db = getDbTable(table);
    if (!db) return { ok: false, error: `Table '${table}' not found`, data: [] };

    let data: unknown;
    switch (action) {
      case 'list':
        data = await db.list(payload ?? {});
        break;
      case 'listIds': {
        const ids: string[] = [];
        let offset = 0;
        const batchSize = 1000;
        while (true) {
          const batch = await db.list({ ...(payload ?? {}), limit: batchSize, offset });
          if (!Array.isArray(batch) || batch.length === 0) break;
          for (const row of batch) if (row?.id != null) ids.push(String(row.id));
          if (batch.length < batchSize) break;
          offset += batchSize;
        }
        data = ids;
        break;
      }
      case 'count':
        data = await db.count(payload ?? undefined);
        break;
      case 'get':
        data = await db.get(payload?.id);
        break;
      case 'upsert':
        data = await db.upsert(payload);
        break;
      case 'delete':
        data = await db.delete(payload?.id);
        break;
      case 'deleteAll': {
        let deleted = 0;
        while (true) {
          const batch = await db.list({ limit: 500 });
          if (!Array.isArray(batch) || batch.length === 0) break;
          for (const row of batch) {
            if (row?.id) {
              await db.delete(row.id);
              deleted += 1;
            }
          }
        }
        data = { deleted };
        break;
      }
    }

    return { ok: true, data: data as T };
  } catch (e: any) {
    if (logErrors) console.error(`[adminDb] ${action} ${table} exception:`, e?.message);
    return { ok: false, error: e?.message ?? 'Database request failed', data: [] };
  }
}

// ─── DB Test ───────────────────────────────────────────────────────────────

export async function dbTestWriteRead(tableName: string): Promise<{ ok: boolean; message: string }> {
  const testId = `test_${Date.now()}`;
  const now = new Date().toISOString();

  const testPayloads: Record<string, Record<string, unknown>> = {
    cmsBlocks: { pageKey: 'test', blockKey: 'test', lang: 'ru', contentJson: '{}', updatedAt: now },
    catalogProducts: { series: 'test', slug: 'test-slug', nameJson: '{}', image: '', palette: '', colorTag: '', groupType: '', installationType: '', layingTypesJson: '[]', collection: '', compatibilityJson: '[]', code: 'TEST', specsJson: '{}', updatedAt: now },
    portfolioItems: { titleJson: '{}', cityJson: '{}', area: 0, productType: 'test', imagesJson: '[]', updatedAt: now },
    newsItems: { titleJson: '{}', excerptJson: '{}', bodyJson: '{}', coverImage: '', date: now, updatedAt: now },
    whereToBuyPoints: { title: 'Test Point', lat: 0, lng: 0, address: 'test', phone: '', type: 'dealer', metaJson: '{}', updatedAt: now },
  };

  const payload = testPayloads[tableName];
  if (!payload) return { ok: false, message: `Unknown table: ${tableName}` };

  const upsertResult = await proxyCall('upsert', tableName, { id: testId, ...payload });
  if (!upsertResult.ok) return { ok: false, message: `Upsert failed: ${upsertResult.error}` };

  const getResult = await proxyCall('get', tableName, { id: testId });
  if (!getResult.ok) return { ok: false, message: `Get failed: ${getResult.error}` };

  await proxyCall('delete', tableName, { id: testId });

  return { ok: true, message: `✓ Запись создана, прочитана и удалена (id: ${testId})` };
}
